import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/authContext";
import {
  Sparkles,
  LayoutDashboard,
  MessageSquare,
  Ticket,
  BookOpen,
  LogOut,
  ScanSearch,
  Users,
} from "lucide-react";
import { LegalFooter } from "@/components/LegalFooter";

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const navItems = [
    { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true, id: "nav-dashboard" },
    { to: "/app/chat", icon: MessageSquare, label: "AI Assistant", id: "nav-chat" },
    { to: "/app/diagnose", icon: ScanSearch, label: "Screenshot Diagnosis", id: "nav-diagnose" },
    { to: "/app/tickets", icon: Ticket, label: "My Tickets", id: "nav-tickets" },
    ...(user?.role === "owner" ? [{ to: "/app/team", icon: Users, label: "Team Tickets", id: "nav-team" }] : []),
    { to: "/app/kb", icon: BookOpen, label: "Knowledge Base", id: "nav-kb" },
  ];

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-zinc-200 fixed inset-y-0 left-0 flex flex-col" data-testid="sidebar">
        <Link to="/" className="flex items-center gap-2 p-6 border-b border-zinc-100" data-testid="sidebar-logo">
          <div className="w-8 h-8 bg-zinc-900 rounded-md flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-base tracking-tighter leading-tight">NextGen<span className="text-blue-600"> Resolution Experts</span></span>
        </Link>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                  isActive
                    ? "bg-zinc-900 text-white"
                    : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={2} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-zinc-100">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-50" data-testid="sidebar-user">
            <div className="w-9 h-9 bg-blue-600 text-white font-bold rounded-full flex items-center justify-center text-sm">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-zinc-900 truncate">{user?.name}</div>
              <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
            </div>
            <button
              onClick={() => {
                logout();
                nav("/");
              }}
              className="p-2 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-200 rounded-lg transition"
              data-testid="logout-btn"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 ml-64 flex flex-col min-h-screen">
        <div className="flex-1">
          <Outlet />
        </div>
        <LegalFooter className="px-8 pb-6 mt-0" />
      </main>
    </div>
  );
}
